import React from "react";
import { Card, CardHeader, CardContent, Typography, Grid, Box } from "@mui/material";

// Keep the same pest order
const PEST_OPTIONS = ["Roaches", "Mosq", "Rodents", "Flies", "Ants", "Others"];

const tidy = (v) => (v == null ? "" : String(v).trim());

/**
 * View-only Location card (Makati GPC)
 * - Smaller labels, larger values
 * - More space between label & value
 * - Skips entirely if location is blank
 */
export default function ViewLocationCard({ loc }) {
  if (!loc?.location || !loc.location.trim()) return null;

  // Compute pest counts and total per location
  const pestCounts = PEST_OPTIONS.map((type) => ({
    type,
    count: Number(loc?.pests?.[type]) || 0,
  }));
  const totalCount = pestCounts.reduce((sum, p) => sum + p.count, 0);

  const displayFindings =
    loc?.findings === "Other" ? tidy(loc?.findings_other) : tidy(loc?.findings);

  const displayRecommendation =
    loc?.recommendation === "Other"
      ? tidy(loc?.recommendation_other)
      : tidy(loc?.recommendation);

  // Helper for label/value rows
  const Row = ({ label, value }) => (
    <Box sx={{ display: "flex", gap: 2, mb: 0.5, alignItems: "baseline" }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ minWidth: 150, flexShrink: 0 }}
      >
        {label}
      </Typography>
      <Typography variant="body2">{value || "—"}</Typography>
    </Box>
  );

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardHeader
        title={
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {loc.location}
          </Typography>
        }
        sx={{ pb: 0, px: 2, pt: 2 }}
      />
      <CardContent sx={{ px: 2, pt: 1, pb: 2 }}>
        {/* Pest Count grid: small labels, emphasized values */}
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
          Pest Count
        </Typography>
        <Grid container spacing={1.5} sx={{ mb: 1 }}>
          {pestCounts.map(({ type, count }) => (
            <Grid key={type} item xs={6} sm={4} md={2}>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                {type}
              </Typography>
              <Typography variant="body2">{count}</Typography>
            </Grid>
          ))}
        </Grid>

        <Row label="Total Pest Count" value={String(totalCount)} />
        <Row label="Findings" value={displayFindings} />
        <Row label="Recommendation" value={displayRecommendation} />
      </CardContent>
    </Card>
  );
}
